import { Component, OnInit } from '@angular/core';

import { AuthenticationService } from '../services/authentication.service';
import { EUserRole, MenuItem } from '../utils';
import { Router, RoutesRecognized } from '@angular/router';
import { User } from '../models/user';
import { environment } from 'src/environments/environment';
import { filter } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DataSharedStorageService } from '../services/data-shared-storage.service';

export interface ILanguageOption {
  code: string;
  label: string;
}

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit {
  currentUser: User | undefined;
  isCollapsed: boolean = false;
  resized: boolean = false;
  currentApplicationVersion = environment.appVersion;

  availableLanguages: ILanguageOption[] = [
    { code: 'fr', label: 'Français' },
    { code: 'en', label: 'English' },
    { code: 'pt', label: 'Português' },
  ];

  MENU_ITEMS: MenuItem[] = [
    {
      url: '',
      icon: 'house',
      title: 'menu.home',
      authorizedRole: ['all'],
      routerLinkActiveOptions: true,
    },
    {
      url: '',
      icon: 'list',
      title: 'menu.marketingIndicators',
      collapsedId: 'others',
      authorizedRole: [EUserRole.ADMIN],
      routerLinkActiveOptions: false,
      children: [
        {
          url: 'others',
          icon: 'boxes',
          title: 'menu.otherProducts',
          authorizedRole: [EUserRole.ADMIN],
          routerLinkActiveOptions: true,
        },
        {
          url: 'others/others-types',
          icon: 'grid',
          title: 'menu.productCategories',
          authorizedRole: [EUserRole.ADMIN],
          routerLinkActiveOptions: false,
        },
        {
          url: 'others/others-countries',
          icon: 'flag',
          title: 'menu.otherProductsByCountry',
          authorizedRole: [EUserRole.ADMIN],
          routerLinkActiveOptions: false,
        },
      ],
    },
    {
      url: '',
      icon: 'graph-up',
      title: 'menu.kpis',
      collapsedId: 'kpis',
      authorizedRole: [EUserRole.ADMIN, 'all', EUserRole.PRE_COMMANDER, EUserRole.SUPERVISOR, EUserRole.ZONE_CHIEF, EUserRole.COUNTRY_MANAGER, EUserRole.MARKETING_AGENT],
      routerLinkActiveOptions: false,
      children: [
        {
          url: 'goals',
          icon: 'speedometer',
          title: 'menu.goals',
          authorizedRole: [EUserRole.ADMIN, 'all', EUserRole.PRE_COMMANDER, EUserRole.SUPERVISOR, EUserRole.ZONE_CHIEF, EUserRole.COUNTRY_MANAGER, EUserRole.MARKETING_AGENT],
          routerLinkActiveOptions: false,
        },
        {
          url: 'performances',
          icon: 'graph-up',
          title: 'menu.performances',
          authorizedRole: [EUserRole.ADMIN, EUserRole.PRE_COMMANDER, EUserRole.SUPERVISOR, EUserRole.ZONE_CHIEF, EUserRole.COUNTRY_MANAGER, EUserRole.MARKETING_AGENT],
          routerLinkActiveOptions: false,
        }
      ],
    },
    {
      url: '',
      icon: 'truck',
      title: 'menu.toursAndLoadings',
      collapsedId: 'tourneesAndLoadings',
      authorizedRole: [
        EUserRole.ADMIN,
        EUserRole.SUPERVISOR,
        EUserRole.SHIFT_SUPERVISOR,
        EUserRole.ZONE_CHIEF,
        EUserRole.COUNTRY_MANAGER,
        EUserRole.PRE_COMMANDER,
        EUserRole.BILLING_MANAGER,
        EUserRole.BILLING_AGENT,
        EUserRole.LEAD_CASH
      ],
      routerLinkActiveOptions: false,
      children: [
        {
          url: 'tournees',
          icon: 'truck',
          title: 'menu.tours',
          authorizedRole: [
            EUserRole.ADMIN,
            EUserRole.SUPERVISOR,
            EUserRole.ZONE_CHIEF,
            EUserRole.COUNTRY_MANAGER,
            EUserRole.BILLING_MANAGER,
            EUserRole.BILLING_AGENT,
            EUserRole.LEAD_CASH
          ],
          routerLinkActiveOptions: false,
        },
        {
          url: 'unloading-bills',
          icon: 'truck',
          title: 'menu.unloadingInvoices',
          authorizedRole: [
            EUserRole.ADMIN,
            EUserRole.SUPERVISOR,
            EUserRole.ZONE_CHIEF,
            EUserRole.COUNTRY_MANAGER,
            EUserRole.SHIFT_SUPERVISOR,
            EUserRole.BILLING_MANAGER,
            EUserRole.BILLING_AGENT,
            EUserRole.LEAD_CASH
          ],
          routerLinkActiveOptions: false,
        },
        {
          url: 'request-loadings/loading-requests',
          icon: 'truck',
          title: 'menu.loadingRequests',
          authorizedRole: [
            EUserRole.ADMIN,
            EUserRole.SUPERVISOR,
            EUserRole.ZONE_CHIEF,
            EUserRole.COUNTRY_MANAGER,
            EUserRole.SHIFT_SUPERVISOR,
            EUserRole.BILLING_MANAGER,
            EUserRole.BILLING_AGENT,
            EUserRole.LEAD_CASH
          ],
          routerLinkActiveOptions: false,
        },
        {
          url: 'forecasts',
          icon: 'arrow-clockwise',
          title: 'menu.loadingForecasts',
          authorizedRole: [
            EUserRole.ADMIN,
            EUserRole.SUPERVISOR,
            EUserRole.ZONE_CHIEF,
            EUserRole.COUNTRY_MANAGER
          ],
          routerLinkActiveOptions: false,
        },
        {
          url: 'orders',
          icon: 'clipboard2-check',
          title: 'menu.purchaseOrdersMt',
          authorizedRole: [EUserRole.ADMIN, EUserRole.PRE_COMMANDER, EUserRole.ZONE_CHIEF, EUserRole.COUNTRY_MANAGER, EUserRole.SUPERVISOR],
          routerLinkActiveOptions: false,
        },
        {
          url: 'loadings',
          icon: 'truck',
          title: 'menu.truckLoadingsMt',
          authorizedRole: [EUserRole.ADMIN, EUserRole.PRE_COMMANDER, EUserRole.ZONE_CHIEF, EUserRole.COUNTRY_MANAGER, EUserRole.SUPERVISOR],
          routerLinkActiveOptions: false,
        },
        {
          url: 'request-loadings/loading-logistics',
          icon: 'truck',
          title: 'menu.loadings',
          authorizedRole: [
            EUserRole.ADMIN,
            EUserRole.SUPERVISOR,
            EUserRole.ZONE_CHIEF,
            EUserRole.COUNTRY_MANAGER,
            EUserRole.SHIFT_SUPERVISOR,
            EUserRole.BILLING_MANAGER,
            EUserRole.BILLING_AGENT,
            EUserRole.LEAD_CASH
          ],
          routerLinkActiveOptions: false,
        },
        {
          url: 'request-loadings/additionnal-loading-logistics',
          icon: 'truck',
          title: 'menu.additionalLoadings',
          authorizedRole: [
            EUserRole.ADMIN,
            EUserRole.SUPERVISOR,
            EUserRole.ZONE_CHIEF,
            EUserRole.COUNTRY_MANAGER,
            EUserRole.SHIFT_SUPERVISOR,
            EUserRole.BILLING_MANAGER,
            EUserRole.BILLING_AGENT,
            EUserRole.LEAD_CASH
          ],
          routerLinkActiveOptions: false,
        },
      ],
    },
    {
      url: 'sales',
      icon: 'shop',
      title: 'menu.sales',
      authorizedRole: [
        EUserRole.ADMIN,
        EUserRole.SUPERVISOR,
        EUserRole.ZONE_CHIEF,
        EUserRole.COUNTRY_MANAGER,
        EUserRole.BUSINESS_DEVELOPPER,
        EUserRole.PRE_COMMANDER,
        EUserRole.BILLING_MANAGER,
        EUserRole.BILLING_AGENT,
        EUserRole.LEAD_CASH
      ],
      routerLinkActiveOptions: false,
    },
    {
      url: '',
      icon: 'shop',
      title: 'menu.pointsOfSale',
      collapsedId: 'point_of_sales',
      authorizedRole: [
        EUserRole.ADMIN,
        EUserRole.SUPERVISOR,
        EUserRole.ZONE_CHIEF,
        EUserRole.COUNTRY_MANAGER,
        EUserRole.BUSINESS_DEVELOPPER,
        EUserRole.PRE_COMMANDER,
        EUserRole.BILLING_MANAGER,
        EUserRole.BILLING_AGENT,
        EUserRole.LEAD_CASH,
        EUserRole.MARKETING_AGENT
      ],
      routerLinkActiveOptions: false,
      children: [
        {
          url: 'point_of_sales_tradi',
          icon: 'shop',
          title: 'menu.pointsOfSaleTradi',
          authorizedRole: [
            EUserRole.ADMIN,
            EUserRole.SUPERVISOR,
            EUserRole.ZONE_CHIEF,
            EUserRole.COUNTRY_MANAGER,
            EUserRole.BUSINESS_DEVELOPPER,
            EUserRole.BILLING_MANAGER,
            EUserRole.BILLING_AGENT,
            EUserRole.LEAD_CASH,
            EUserRole.MARKETING_AGENT
          ],
          routerLinkActiveOptions: false,
        },
        {
          url: 'point-of-sales',
          icon: 'shop',
          title: 'menu.mt',
          authorizedRole: [
            EUserRole.ADMIN,
            EUserRole.PRE_COMMANDER,
            EUserRole.BILLING_MANAGER,
            EUserRole.BILLING_AGENT,
            EUserRole.LEAD_CASH,
            EUserRole.SUPERVISOR,
            EUserRole.ZONE_CHIEF,
            EUserRole.COUNTRY_MANAGER,
            EUserRole.MARKETING_AGENT
          ],
          routerLinkActiveOptions: false,
        },
        {
          url: 'stores-checks',
          icon: 'shop',
          title: 'menu.storesChecks',
          authorizedRole: [EUserRole.ADMIN, EUserRole.PRE_COMMANDER, EUserRole.SUPERVISOR, EUserRole.ZONE_CHIEF, EUserRole.COUNTRY_MANAGER, EUserRole.MARKETING_AGENT],
          routerLinkActiveOptions: false,
        },
        {
          url: 'trade-schedules',
          icon: 'calendar3',
          title: 'menu.tradeSchedulesMt',
          authorizedRole: [
            EUserRole.ADMIN,
            EUserRole.PRE_COMMANDER,
            EUserRole.BILLING_MANAGER,
            EUserRole.BILLING_AGENT,
            EUserRole.LEAD_CASH,
            EUserRole.SUPERVISOR,
            EUserRole.ZONE_CHIEF,
            EUserRole.COUNTRY_MANAGER,
            EUserRole.MARKETING_AGENT
          ],
          routerLinkActiveOptions: false,
        },
        {
          url: 'payment-modes',
          icon: 'currency-exchange',
          title: 'menu.paymentModes',
          authorizedRole: [
            EUserRole.ADMIN,
            EUserRole.PRE_COMMANDER,
            EUserRole.BILLING_MANAGER,
            EUserRole.BILLING_AGENT,
            EUserRole.LEAD_CASH,
            EUserRole.MARKETING_AGENT
          ],
          routerLinkActiveOptions: false,
        },
      ],
    },
    {
      url: '',
      icon: 'diagram-2',
      title: 'menu.clustering',
      collapsedId: 'clusterisation',
      authorizedRole: [EUserRole.ADMIN, EUserRole.COUNTRY_MANAGER],
      routerLinkActiveOptions: false,
      children: [
        {
          url: 'clusters',
          icon: 'diagram-2',
          title: 'menu.clusters',
          authorizedRole: [EUserRole.ADMIN, EUserRole.COUNTRY_MANAGER],
          routerLinkActiveOptions: false,
        },
        {
          url: 'clusters-rtm',
          icon: 'diagram-2',
          title: 'menu.clustersRtm',
          authorizedRole: [EUserRole.ADMIN, EUserRole.COUNTRY_MANAGER],
          routerLinkActiveOptions: false,
        },
      ],
    },
    {
      url: '',
      icon: 'boxes',
      title: 'menu.stockManagement',
      collapsedId: 'stock-management',
      authorizedRole: [EUserRole.ADMIN, EUserRole.SHIFT_SUPERVISOR, EUserRole.PLANNER, EUserRole.COUNTRY_MANAGER, EUserRole.SUPERVIOR_LOGISTIC],
      routerLinkActiveOptions: false,
      children: [
        {
          url: 'stock-movements',
          icon: 'boxes',
          title: 'menu.stockMovements',
          authorizedRole: [EUserRole.ADMIN, EUserRole.SHIFT_SUPERVISOR],
          routerLinkActiveOptions: true,
        },
        {
          url: 'tours/stock-receipt-list',
          icon: 'boxes',
          title: 'menu.stockReceipts',
          authorizedRole: [EUserRole.ADMIN, EUserRole.SHIFT_SUPERVISOR],
          routerLinkActiveOptions: true,
        },
        {
          url: 'stock-controls',
          icon: 'truck',
          title: 'menu.stockControls',
          authorizedRole: [EUserRole.ADMIN, EUserRole.SHIFT_SUPERVISOR],
          routerLinkActiveOptions: false,
        },
        {
          url: 'inventories',
          icon: 'truck',
          title: 'menu.inventories',
          authorizedRole: [EUserRole.ADMIN, EUserRole.SHIFT_SUPERVISOR, EUserRole.PLANNER, EUserRole.COUNTRY_MANAGER, EUserRole.SUPERVIOR_LOGISTIC],
          routerLinkActiveOptions: false,
        },
      ],
    },
    {
      url: '',
      icon: 'list',
      title: 'menu.referenceData',
      collapsedId: 'référentiels',
      authorizedRole: [EUserRole.ADMIN, EUserRole.SHIFT_SUPERVISOR, EUserRole.COUNTRY_MANAGER],
      routerLinkActiveOptions: false,
      children: [
        {
          url: 'countries',
          icon: 'flag',
          title: 'menu.countries',
          authorizedRole: [EUserRole.ADMIN, EUserRole.COUNTRY_MANAGER],
          routerLinkActiveOptions: false,
        },
        {
          url: 'commercial-regions',
          icon: 'flag',
          title: 'menu.commercialRegions',
          authorizedRole: [EUserRole.ADMIN, EUserRole.SHIFT_SUPERVISOR, EUserRole.COUNTRY_MANAGER],
          routerLinkActiveOptions: false,
        },
        {
          url: 'depots',
          icon: 'bank',
          title: 'menu.depots',
          authorizedRole: [EUserRole.ADMIN, EUserRole.COUNTRY_MANAGER],
          routerLinkActiveOptions: false,
        },
        {
          url: 'zone-supervisors',
          icon: 'map',
          title: 'menu.zoneSupervisors',
          authorizedRole: [EUserRole.ADMIN, EUserRole.SHIFT_SUPERVISOR, EUserRole.COUNTRY_MANAGER],
          routerLinkActiveOptions: false,
        },
        {
          url: 'sectors',
          icon: 'columns-gap',
          title: 'menu.sectors',
          authorizedRole: [EUserRole.ADMIN, EUserRole.SHIFT_SUPERVISOR, EUserRole.COUNTRY_MANAGER],
          routerLinkActiveOptions: false,
        },
        {
          url: 'zones',
          icon: 'pin-map',
          title: 'menu.zones',
          authorizedRole: [EUserRole.ADMIN, EUserRole.COUNTRY_MANAGER],
          routerLinkActiveOptions: false,
        },
        {
          url: 'catalogs',
          icon: 'flag',
          title: 'menu.catalogs',
          authorizedRole: [EUserRole.ADMIN, EUserRole.COUNTRY_MANAGER],
          routerLinkActiveOptions: false,
        },
        {
          url: 'categories',
          icon: 'diagram-2',
          title: 'menu.categories',
          authorizedRole: [EUserRole.ADMIN, EUserRole.COUNTRY_MANAGER],
          routerLinkActiveOptions: false,
        },
        {
          url: 'products',
          icon: 'diagram-2',
          title: 'menu.productsAndAccessories',
          authorizedRole: [EUserRole.ADMIN, EUserRole.COUNTRY_MANAGER],
          routerLinkActiveOptions: false,
        },
        {
          url: 'distribution-networks',
          icon: 'diagram-2',
          title: 'menu.distributionNetworks',
          authorizedRole: [EUserRole.ADMIN, EUserRole.COUNTRY_MANAGER],
          routerLinkActiveOptions: false,
        },
      ],
    },
    {
      url: 'tour-management',
      icon: 'list',
      title: 'menu.logisticPlanning',
      collapsedId: 'tour-management',
      authorizedRole: [EUserRole.ADMIN],
      routerLinkActiveOptions: false,
      children: [
        {
          url: 'gestion-of-trucks',
          icon: 'truck',
          title: 'menu.trucks',
          authorizedRole: [EUserRole.ADMIN],
          routerLinkActiveOptions: false,
        },
        {
          url: 'list-tour-logistic',
          icon: 'flag',
          title: 'menu.logisticTours',
          authorizedRole: [EUserRole.ADMIN],
          routerLinkActiveOptions: false,
        },
        {
          url: 'plannings',
          icon: 'calendar3',
          title: 'menu.weeklyPlannings',
          authorizedRole: [EUserRole.ADMIN],
          routerLinkActiveOptions: true,
        },
      ],
    },
    {
      url: '',
      icon: 'gear',
      title: 'menu.administration',
      authorizedRole: [EUserRole.ADMIN],
      routerLinkActiveOptions: false,
      collapsedId: 'administration',
      children: [
        {
          url: 'users',
          icon: 'people',
          title: 'menu.users',
          authorizedRole: [EUserRole.ADMIN],
          routerLinkActiveOptions: false,
        },
        {
          url: 'roles',
          icon: 'person-badge',
          title: 'menu.roles',
          authorizedRole: [EUserRole.ADMIN],
          routerLinkActiveOptions: false,
        },
        {
          url: 'permissions',
          icon: 'key',
          title: 'menu.permissions',
          authorizedRole: [EUserRole.ADMIN],
          routerLinkActiveOptions: false,
        },
        {
          url: 'app-version-configs',
          icon: 'phone',
          title: 'menu.appVersions',
          authorizedRole: [EUserRole.ADMIN],
          routerLinkActiveOptions: false,
        },
      ],
    },
    {
      url: '',
      icon: 'list',
      title: 'menu.x3Synchronization',
      collapsedId: 'x3_synchronization',
      authorizedRole: [EUserRole.ADMIN, EUserRole.SHIFT_SUPERVISOR, EUserRole.BILLING_AGENT, EUserRole.BILLING_MANAGER],
      routerLinkActiveOptions: false,
      children: [
        {
          url: 'sage-synchronizations/loadings',
          icon: 'truck',
          title: 'menu.x3Loadings',
          authorizedRole: [EUserRole.ADMIN, EUserRole.SHIFT_SUPERVISOR],
          routerLinkActiveOptions: true,
        },
        {
          url: 'sage-synchronizations/additionnal_loadings',
          icon: 'truck',
          title: 'menu.x3AdditionalLoadings',
          authorizedRole: [EUserRole.ADMIN, EUserRole.SHIFT_SUPERVISOR],
          routerLinkActiveOptions: true,
        },
        {
          url: 'sage-synchronizations/lsp',
          icon: 'arrow-left-right',
          title: 'menu.lspSync',
          authorizedRole: [EUserRole.ADMIN, EUserRole.SHIFT_SUPERVISOR, EUserRole.BILLING_AGENT, EUserRole.BILLING_MANAGER],
          routerLinkActiveOptions: true,
        },
      ],
    },
    {
      url: '',
      icon: 'currency-dollar',
      title: 'menu.billingAndTreasury',
      authorizedRole: [
        EUserRole.ADMIN,
        EUserRole.SUPERVISOR,
        EUserRole.BUSINESS_DEVELOPPER,
        EUserRole.BILLING_MANAGER,
        EUserRole.BILLING_AGENT,
        EUserRole.TREASURY_MANAGER,
        EUserRole.DAF,
        EUserRole.BILLING_AGENT,
        EUserRole.LEAD_CASH,
        EUserRole.COUNTRY_MANAGER
      ],
      routerLinkActiveOptions: false,
      collapsedId: 'facturation',
      children: [
        {
          url: 'transactions',
          icon: 'currency-exchange',
          title: 'menu.transactions',
          authorizedRole: [
            EUserRole.ADMIN,
            EUserRole.TREASURY_MANAGER,
            EUserRole.DAF,
            EUserRole.BILLING_MANAGER,
            EUserRole.BILLING_AGENT,
            EUserRole.LEAD_CASH
          ],
          routerLinkActiveOptions: false,
        },
        {
          url: 'seller-references',
          icon: 'people',
          title: 'menu.sellerReferences',
          authorizedRole: [
            EUserRole.ADMIN,
            EUserRole.TREASURY_MANAGER,
            EUserRole.DAF,
            EUserRole.BILLING_MANAGER,
            EUserRole.BILLING_AGENT,
            EUserRole.LEAD_CASH
          ],
          routerLinkActiveOptions: false,
        },
        {
          url: 'accounting-entries',
          icon: 'file-text',
          title: 'menu.accountingEntries',
          authorizedRole: [EUserRole.ADMIN, EUserRole.TREASURY_MANAGER, EUserRole.DAF],
          routerLinkActiveOptions: false,
        },
        {
          url: 'billing-transactions',
          icon: 'currency-exchange',
          title: 'menu.merchantDepositsAndPayments',
          authorizedRole: [
            EUserRole.ADMIN,
            EUserRole.TREASURY_MANAGER,
            EUserRole.DAF,
            EUserRole.BILLING_MANAGER,
            EUserRole.BILLING_AGENT,
            EUserRole.LEAD_CASH],
          routerLinkActiveOptions: false,
        },
        {
          url: 'reconciliation-report',
          icon: 'list',
          title: 'menu.waveReconciliation',
          authorizedRole: [
            EUserRole.ADMIN,
            EUserRole.SUPERVISOR,
            EUserRole.BUSINESS_DEVELOPPER,
            EUserRole.BILLING_MANAGER,
            EUserRole.BILLING_AGENT,
            EUserRole.LEAD_CASH,
            EUserRole.COUNTRY_MANAGER
          ],
          routerLinkActiveOptions: false,
        },
        {
          url: 'sector-payment-configs/list',
          icon: 'credit-card',
          title: 'menu.paymentConfigs',
          authorizedRole: [EUserRole.ADMIN, EUserRole.TREASURY_MANAGER, EUserRole.DAF],
          routerLinkActiveOptions: false,
        },
        {
          url: 'facturation-vmc',
          icon: 'journal-check',
          title: 'menu.endOfDayClosing',
          authorizedRole: [
            EUserRole.ADMIN,
            EUserRole.TREASURY_MANAGER,
            EUserRole.BILLING_AGENT,
            EUserRole.LEAD_CASH,
            EUserRole.BILLING_MANAGER,
          ],
          routerLinkActiveOptions: false,
        },
        {
          url: 'bl-br',
          icon: 'file-earmark-text',
          title: 'menu.blBrVerification',
          authorizedRole: [
            EUserRole.ADMIN,
            EUserRole.TREASURY_MANAGER,
            EUserRole.DAF,
            EUserRole.BILLING_AGENT,
            EUserRole.LEAD_CASH,
            EUserRole.COUNTRY_MANAGER
          ],
          routerLinkActiveOptions: false,
        },
        {
          url: '',
          icon: 'gear',
          title: 'menu.billingAdministration',
          authorizedRole: [EUserRole.ADMIN],
          routerLinkActiveOptions: false,
          collapsedId: 'administration',
          children: [
            {
              url: 'users',
              icon: 'people',
              title: 'menu.billingUsersManagement',
              authorizedRole: [EUserRole.ADMIN],
              routerLinkActiveOptions: false,
            },
            {
              url: 'roles',
              icon: 'person-badge',
              title: 'menu.billingRolesManagement',
              authorizedRole: [EUserRole.ADMIN],
              routerLinkActiveOptions: false,
            },
            {
              url: 'permissions',
              icon: 'key',
              title: 'menu.billingPermissionsManagement',
              authorizedRole: [EUserRole.ADMIN],
              routerLinkActiveOptions: false,
            },
          ],
        },
      ],
    },
    {
      url: 'survey',
      icon: 'question',
      title: 'menu.surveys',
      authorizedRole: [EUserRole.ADMIN, EUserRole.SUPERVISOR, EUserRole.ZONE_CHIEF, EUserRole.COUNTRY_MANAGER],
      routerLinkActiveOptions: false,
    },
    {
      url: '',
      icon: 'chat-dots',
      title: 'menu.whatsapp',
      collapsedId: 'whatsapp',
      authorizedRole: [EUserRole.ADMIN, EUserRole.ZONE_CHIEF, EUserRole.COUNTRY_MANAGER, EUserRole.SUPERVISOR, EUserRole.MARKETING_AGENT],
      routerLinkActiveOptions: false,
      children: [
        { url: 'whatsapp/reporting', icon: 'list', title: 'menu.whatsappSentTracking', authorizedRole: [EUserRole.ADMIN, EUserRole.ZONE_CHIEF, EUserRole.COUNTRY_MANAGER, EUserRole.SUPERVISOR, EUserRole.MARKETING_AGENT], routerLinkActiveOptions: false },
        { url: 'whatsapp/messages-recus', icon: 'table', title: 'menu.whatsappReceivedMessages', authorizedRole: [EUserRole.ADMIN, EUserRole.ZONE_CHIEF, EUserRole.COUNTRY_MANAGER, EUserRole.SUPERVISOR, EUserRole.MARKETING_AGENT], routerLinkActiveOptions: false },
        { url: 'whatsapp-inbound', icon: 'envelope-open', title: 'menu.whatsappInbound', authorizedRole: [EUserRole.ADMIN, EUserRole.ZONE_CHIEF, EUserRole.COUNTRY_MANAGER, EUserRole.SUPERVISOR, EUserRole.MARKETING_AGENT], routerLinkActiveOptions: false, openInNewTab: true },
      ],
    },
  ];

  mapMenuItem: MenuItem = {
    url: 'maps',
    icon: 'map',
    title: 'menu.viewMap',
    authorizedRole: [EUserRole.ADMIN, EUserRole.SUPERVISOR, EUserRole.ZONE_CHIEF, EUserRole.COUNTRY_MANAGER],
    routerLinkActiveOptions: false,
  };

  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
    private translateService: TranslateService,
    private dataSharedStorageService: DataSharedStorageService
  ) {
    this.authenticationService.currentUser.subscribe(
      (x) => (this.currentUser = x)
    );
  }

  ngOnInit(): void {
    this.router.events
      .pipe(filter((event) => event instanceof RoutesRecognized))
      .subscribe((event) => {
        this.authenticationService.me();
      });
  }

  logout() {
    this.authenticationService.logout();
    this.router.navigate(['/login']);
  }

  get currentLang(): string {
    return this.translateService.currentLang || this.translateService.defaultLang;
  }

  get currentLanguageOption(): ILanguageOption {
    return (
      this.availableLanguages.find((lang) => lang.code === this.currentLang) ||
      this.availableLanguages[0]
    );
  }

  changeLanguage(lang: string): void {
    if (lang === this.currentLang) return;
    this.dataSharedStorageService.setItem('currentLanguage', lang);
    window.location.reload();
  }

  canViewMenu(menu: MenuItem) {
    return (
      menu.authorizedRole.includes(this.currentUser!.role?.libelle!) ||
      menu.authorizedRole.includes('all')
    );
  }

  resize() {
    this.resized = !this.resized;
  }
}
